export type GbpConnectErrorInfo = {
  code: string;
  title: string;
  message: string;
  tips: string[];
  isUserFixable: boolean;
};

function decodeParam(raw: string | null | undefined): string {
  if (!raw) return '';
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' ')).trim();
  } catch {
    return raw.replace(/\+/g, ' ').trim();
  }
}

/** Map Zernio/Google OAuth + our edge-function errors into user-facing copy. */
export function formatGbpConnectError(
  code: string | null | undefined,
  message?: string | null,
  opts?: { isUserFixable?: boolean | null },
): GbpConnectErrorInfo {
  const normalized = String(code || 'unknown')
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  const decoded = decodeParam(message || '');
  const isUserFixable =
    typeof opts?.isUserFixable === 'boolean' ? opts.isUserFixable : true;

  if (normalized === 'oauth_denied' || normalized === 'access_denied') {
    return {
      code: normalized,
      title: 'Connection cancelled',
      message: 'You cancelled Google sign-in, or Google denied access.',
      tips: ['Try again and approve access for Google Business Profile.'],
      isUserFixable: true,
    };
  }

  if (normalized === 'google_permission_denied') {
    return {
      code: normalized,
      title: 'Google blocked Business Profile access',
      message:
        decoded ||
        'Google blocked Business Profile API access for this Google account.',
      tips: [
        'If this is a Google Workspace account, ask your Workspace admin to enable Google Business Profile for your user.',
        'Ask the admin to allow third-party app access (or allow this connect app) under security settings.',
        'Or sign in with a different Google account that manages the Business Profile and is not restricted.',
      ],
      isUserFixable: true,
    };
  }

  if (
    normalized === 'payment_required' ||
    normalized === 'zernio_http_402' ||
    normalized === 'free_tier_exceeded' ||
    /payment required|free.?tier|add a payment method|402/i.test(decoded)
  ) {
    return {
      code: normalized === 'unknown' ? 'payment_required' : normalized,
      title: 'Zernio billing limit reached',
      message:
        decoded ||
        'Zernio blocked this connect (HTTP 402). The free plan allows 2 connected accounts — extra connects need a payment method, or unused Zernio accounts need to be deleted.',
      tips: [
        'Open your Zernio dashboard → Billing and add a payment method, or',
        'Delete unused Google Business accounts in Zernio (old test reconnects still count), then try again.',
        'Nexus disconnect does not always remove the account from Zernio if another location still uses it.',
      ],
      isUserFixable: true,
    };
  }

  if (
    /already connected to another/i.test(decoded) ||
    normalized === 'account_in_use'
  ) {
    return {
      code: normalized === 'unknown' ? 'account_in_use' : normalized,
      title: 'Location already connected elsewhere',
      message:
        decoded ||
        'That Google Business location is already connected to another Nexus business.',
      tips: [
        'Open the other Nexus business and disconnect Google Business there first.',
        'Then come back and connect it to this business.',
      ],
      isUserFixable: true,
    };
  }

  if (/switched businesses/i.test(decoded)) {
    return {
      code: 'business_mismatch',
      title: 'Wrong business selected',
      message: decoded,
      tips: ['Switch back to the business you started connecting from, then try again.'],
      isUserFixable: true,
    };
  }

  if (
    /no gbp location|not on this business/i.test(decoded) ||
    normalized === 'no_location' ||
    normalized === 'google_business_no_locations'
  ) {
    return {
      code: normalized === 'unknown' ? 'no_location' : normalized,
      title: 'No Google location found',
      message:
        decoded ||
        'Google signed in, but no Business Profile location is available for this account yet.',
      tips: [
        'Finish picking your location in the location picker — don’t close early.',
        'Make sure this Google account manages a Google Business Profile.',
        'Try connecting again.',
      ],
      isUserFixable: true,
    };
  }

  if (decoded) {
    return {
      code: normalized,
      title: 'Couldn’t connect Google Business',
      message: decoded,
      tips: isUserFixable
        ? ['Fix the issue above, then try connecting again.']
        : ['Try again in a few minutes. If it keeps failing, contact support.'],
      isUserFixable,
    };
  }

  return {
    code: normalized,
    title: 'Couldn’t connect Google Business',
    message: `Google connect failed (${normalized}).`,
    tips: [
      'Try again. If it keeps failing, contact support with this error code.',
      `Error code: ${normalized}`,
    ],
    isUserFixable: true,
  };
}

/** Build user-facing error from a structured GBP edge-function JSON body. */
export function formatGbpEdgeError(body: {
  error?: unknown;
  code?: unknown;
  stage?: unknown;
  function?: unknown;
} | null): GbpConnectErrorInfo | null {
  if (!body?.error) return null;
  const code = String(body.code || body.stage || 'edge_error');
  const info = formatGbpConnectError(code, String(body.error));
  const extra = [
    body.stage ? `Stage: ${String(body.stage)}` : '',
    body.function ? `Function: ${String(body.function)}` : '',
  ].filter(Boolean);
  return { ...info, tips: [...info.tips, ...extra] };
}

export function parseGbpOauthSearchParams(
  search: string | URLSearchParams,
): {
  error: GbpConnectErrorInfo | null;
  hasOauthReturn: boolean;
  pendingDataToken: string;
  profileId: string;
  connectToken: string | null;
} {
  const params =
    typeof search === 'string' ? new URLSearchParams(search) : search;

  // Prefer rewritten gbp_* keys (index.html renames `error` so Supabase Auth
  // does not treat a GBP failure as a failed login and blank the app).
  const oauthError =
    params.get('gbp_error') ||
    params.get('error');
  const errorMessage =
    params.get('gbp_error_message') ||
    params.get('error_message') ||
    params.get('errorMessage') ||
    '';
  const isUserFixableRaw =
    params.get('gbp_is_user_fixable') ||
    params.get('is_user_fixable') ||
    params.get('isUserFixable');
  const isUserFixable =
    isUserFixableRaw == null
      ? undefined
      : isUserFixableRaw === 'true' || isUserFixableRaw === '1';

  const pendingDataToken =
    params.get('pendingDataToken') || params.get('pending_data_token') || '';
  const profileId = params.get('profileId') || params.get('profile_id') || '';
  const connectToken =
    params.get('connect_token') || params.get('connectToken') || null;

  const platform = (params.get('platform') || '').toLowerCase().replace(/[_-]/g, '');
  const hasOauthReturn =
    params.get('gbp_oauth') === '1' ||
    params.get('gbp_returning') === '1' ||
    platform === 'googlebusiness' ||
    !!oauthError ||
    !!pendingDataToken ||
    params.get('success') === 'google_business_connected' ||
    params.get('connected') === 'googlebusiness';

  if (oauthError) {
    return {
      error: formatGbpConnectError(oauthError, errorMessage, { isUserFixable }),
      hasOauthReturn: true,
      pendingDataToken,
      profileId,
      connectToken,
    };
  }

  return {
    error: null,
    hasOauthReturn,
    pendingDataToken,
    profileId,
    connectToken,
  };
}
